/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */
import './index.css';

import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {useLexicalNodeSelection} from '@lexical/react/useLexicalNodeSelection';
import {mergeRegister} from '@lexical/utils';
import {
  $getNodeByKey,
  $getSelection,
  $isNodeSelection,
  CLICK_COMMAND,
  COMMAND_PRIORITY_HIGH,
  COMMAND_PRIORITY_LOW,
  DecoratorNode,
  DOMConversionMap,
  DOMConversionOutput,
  KEY_BACKSPACE_COMMAND,
  KEY_DELETE_COMMAND,
  LexicalNode,
  NodeKey,
  SerializedLexicalNode,
} from 'lexical';
import * as React from 'react';
import {useCallback, useEffect} from 'react';


function PageBreakComponent({nodeKey}) {
  const [editor] = useLexicalComposerContext();
  const [isSelected, setSelected, clearSelection] =
    useLexicalNodeSelection(nodeKey);

  const $onDelete = useCallback(
    (event) => {
      event.preventDefault();
      if (isSelected && $isNodeSelection($getSelection())) {
        const node = $getNodeByKey(nodeKey);
        if ($isPageBreakNode(node)) {
          node.remove();
          return true;
        }
      }
      return false;
    },
    [isSelected, nodeKey],
  );

  useEffect(() => {
    return mergeRegister(
      editor.registerCommand(
        CLICK_COMMAND,
        (event) => {
          const pbElem = editor.getElementByKey(nodeKey);

          if (event.target === pbElem) {
            if (!event.shiftKey) {
              clearSelection();
            }
            setSelected(!isSelected);
            return true;
          }

          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
      editor.registerCommand(
        KEY_DELETE_COMMAND,
        $onDelete,
        COMMAND_PRIORITY_LOW,
      ),
      editor.registerCommand(
        KEY_BACKSPACE_COMMAND,
        $onDelete,
        COMMAND_PRIORITY_LOW,
      ),
    );
  }, [clearSelection, editor, isSelected, nodeKey, $onDelete, setSelected]);

  useEffect(() => {
    const pbElem = editor.getElementByKey(nodeKey);
    if (pbElem !== null) {
      pbElem.className = isSelected ? 'selected' : '';
    }
  }, [editor, isSelected, nodeKey]);

  return null;
}

export class PageBreakNode extends DecoratorNode {
  __isAuto;
  __heightPx;

  static getType() {
    return 'page-break';
  }

  static clone(node) {
    return new PageBreakNode(node.__isAuto, node.__heightPx, node.__key);
  }

  static importJSON(serializedNode) {
    return $createPageBreakNode(
      Boolean(serializedNode.isAuto),
      typeof serializedNode.heightPx === 'number' ? serializedNode.heightPx : null,
    );
  }

  static importDOM() {
    return {
      figure: (domNode) => {
        const tp = domNode.getAttribute('type');
        if (tp !== this.getType()) {
          return null;
        }

        return {
          conversion: $convertPageBreakElement,
          priority: COMMAND_PRIORITY_HIGH,
        };
      },
    };
  }

  exportJSON() {
    return {
      type: this.getType(),
      isAuto: this.__isAuto,
      heightPx: this.__heightPx,
      version: 1,
    };
  }

  constructor(isAuto = false, heightPx = null, key) {
    super(key);
    this.__isAuto = isAuto;
    this.__heightPx = heightPx;
  }

  createDOM() {
    const el = document.createElement('figure');
    // Print pagination
    el.style.pageBreakAfter = 'always';
    // Modern pagination props
    el.style.breakAfter = 'page';
    el.setAttribute('type', this.getType());

    if (this.__heightPx != null && Number.isFinite(this.__heightPx)) {
      el.style.height = `${this.__heightPx}px`;
    }

    if (this.__isAuto) {
      el.setAttribute('data-auto', 'true');
    }
    return el;
  }

  getTextContent() {
    return '\n';
  }

  isInline() {
    return false;
  }

  updateDOM() {
    return false;
  }

  decorate() {
    return <PageBreakComponent nodeKey={this.__key} />;
  }

  isAuto() {
    return this.__isAuto;
  }

  getHeightPx() {
    return this.__heightPx;
  }
}

function $convertPageBreakElement() {
  return {node: $createPageBreakNode()};
}

export function $createPageBreakNode(isAuto = false, heightPx = null) {
  return new PageBreakNode(isAuto, heightPx);
}

export function $isPageBreakNode(
  node,
) {
  return node instanceof PageBreakNode;
}
